import React, { useState } from 'react';
import { ActionIcon, Menu, Modal, Loader, AspectRatio } from '@mantine/core';
import {
  IconDotsVertical,
  IconDownload,
  IconEyeDown,
} from '@tabler/icons-react';
import { useDownloadFromS3 } from 'hooks';
import { MRT_Row } from 'mantine-react-table';
import { Report } from 'interfaces';
import * as Sentry from '@sentry/react';
import { generateGetSignedUrl } from 'api/action/s3';

interface ReportRowActionsProps {
  row: MRT_Row<Report>;
}

const BUCKET = `report-files-${import.meta.env.VITE_ACCOUNT_ID}`;

export const ReportRowActions: React.FC<ReportRowActionsProps> = ({ row }) => {
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const key = encodeURIComponent(row.original.s3Path);
  const downloadFile = useDownloadFromS3(BUCKET, key, setDownloading);
  const canPreview = row.original.s3Path
    ? row.original.s3Path.toLowerCase().endsWith('.pdf')
    : false;

  const viewFile = async () => {
    try {
      setLoading(true);
      if (row.original.s3Path && canPreview) {
        const url = await generateGetSignedUrl({
          bucket: BUCKET,
          key: key,
        });
        setFileUrl(url);
        setModalOpen(true);
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Menu shadow="md" width={220} withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="transparent"
            aria-label="Actions Menu"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <IconDotsVertical stroke={1.5} size={22} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconDownload size={14} />}
            disabled={!row.original.s3Path || downloading}
            onClick={() => downloadFile()}
          >
            Download
          </Menu.Item>
          <Menu.Item
            leftSection={<IconEyeDown size={14} />}
            disabled={!row.original.s3Path || loading || !canPreview}
            onClick={() => viewFile()}
          >
            View
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        size="xl"
        centered
        closeOnEscape
        closeOnClickOutside
        style={{ borderRadius: 10 }}
      >
        {fileUrl ? (
          <div
            style={{
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <AspectRatio ratio={3 / 3} style={{ width: '100%' }}>
              <iframe
                src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                title="PDF Report Preview"
                style={{
                  flex: 1,
                  border: 'none',
                  padding: '2%',
                }}
              />
            </AspectRatio>
          </div>
        ) : (
          <Loader />
        )}
      </Modal>
    </>
  );
};
